import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import AsyncSelect from 'react-select/async';
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";

const GenericAsyncSelect = forwardRef((props, ref) => {
    const {
        loadOptions,
        onItemClick,
        onClose,
        placeholder = "Selecione uma opção...",
        value = null,
        dataTest = "async-select",
        customOptionComponent = null,
        style = {},
        maintainInputValue = false,
        onInputValueChange = null,
        cacheUniqs = [],
    } = props;

    const [, setLoading] = useRecoilState(loadingAtom);
    const [selectedValue, setSelectedValue] = useState(value);
    const [inputValue, setInputValue] = useState('');
    const selectRef = useRef(null);

    useImperativeHandle(ref, () => ({
        blur: () => {
            selectRef.current?.blur?.();
        },
    }));

    useEffect(() => {
        const nextValue = value?.value ?? value;
        const currentValue = selectedValue?.value ?? selectedValue;
        if (nextValue !== currentValue || value?.label !== selectedValue?.label) {
            setSelectedValue(value);
        }
    }, [value, selectedValue]);

    const handleChange = (selectedOption) => {
        setSelectedValue(selectedOption);
        if (selectedOption && onItemClick) {
            onItemClick(selectedOption.value);
        }
        if (!maintainInputValue) {
            setInputValue('');
        }
    };

    const handleInputChange = (newInputValue, {action}) => {
        if (action === 'input-change') {
            setInputValue(newInputValue);
            if (onInputValueChange) {
                onInputValueChange(newInputValue);
            }
        } else if (action === 'set-value') {
            setInputValue('');
        } else if (action === 'menu-close' && !maintainInputValue) {
            setInputValue('');
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setInputValue('');
            setSelectedValue(null);
            selectRef.current?.blur?.();
        }
    };

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '33px',
            height: '33px',
            width: style.width || '100%',
            maxWidth: '100%',
            border: state.isFocused ? '1px solid #2980b9' : '1px solid #3498db',
            borderRadius: '5px',
            boxShadow: 'none',
            '&:hover': {
                border: '1px solid #2980b9'
            },
            alignItems: 'center'
        }),
        option: (provided) => ({
            ...provided,
            padding: 0,
            backgroundColor: 'transparent',
            cursor: 'pointer',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            width: style.menuWidth || style.width || provided.width,
            minWidth: style.menuMinWidth || style.menuWidth || style.width || provided.width,
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 10050
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#333'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6c757d'
        }),
        indicatorsContainer: () => ({
            display: 'none'
        }),
        dropdownIndicator: () => ({
            display: 'none'
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    return (
        <div data-test={dataTest} style={{width: style.width || '100%', maxWidth: '100%'}}>
            <AsyncSelect
                ref={selectRef}
                cacheOptions
                cacheUniqs={cacheUniqs}
                loadOptions={loadOptions}
                onChange={handleChange}
                onInputChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                value={selectedValue || value}
                inputValue={inputValue}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={customStyles}
                noOptionsMessage={() => null}
                loadingMessage={() => "Carregando..."}
                isClearable
                isSearchable
                defaultMenuIsOpen={false}
                components={customOptionComponent ? {
                    Option: customOptionComponent
                } : undefined}
            />
        </div>
    );
});

GenericAsyncSelect.displayName = 'GenericAsyncSelect';

export default GenericAsyncSelect;
